import React, { useCallback, useEffect, useRef, useState } from 'react';

// 可拖动 / 可缩放的浮动面板 (平台助手对话框用):
// 两种状态: 贴右侧满高 (默认, 只能拖左边改宽) / 浮动窗口 (拖标题栏移动, 拖四边四角缩放).
// 贴边时拖标题栏即变浮动; 位置尺寸记在本机 (只是个人习惯, 读写失败就按默认贴边); 浏览器窗口变小时自动收回视口内.

export interface Box {
    x: number;
    y: number;
    w: number;
    h: number;
}

/** move = 拖标题栏; 其余为缩放方向 (n/s/e/w 及组合) */
export type DragKind = 'move' | 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

const MIN_W = 360;
const MIN_H = 320;

const viewport = () => ({ vw: window.innerWidth, vh: window.innerHeight });

const docked = (width: number): Box => {
    const { vw, vh } = viewport();
    const w = Math.min(width, vw);
    return { x: vw - w, y: 0, w, h: vh };
};

/** 尺寸不小于下限, 整体留在视口内 */
const clamp = (b: Box): Box => {
    const { vw, vh } = viewport();
    const w = Math.min(Math.max(b.w, MIN_W), vw);
    const h = Math.min(Math.max(b.h, MIN_H), vh);
    return {
        w,
        h,
        x: Math.min(Math.max(b.x, 0), vw - w),
        y: Math.min(Math.max(b.y, 0), vh - h),
    };
};

/** 按拖动方向算新框: 拖哪条边动哪条边, 对边不动; 边不出视口, 尺寸不小于下限 */
const resize = (s: Box, kind: DragKind, dx: number, dy: number): Box => {
    if (kind === 'move') return clamp({ ...s, x: s.x + dx, y: s.y + dy });
    const { vw, vh } = viewport();
    let { x, y, w, h } = s;
    if (kind.includes('e')) w = Math.min(Math.max(s.w + dx, MIN_W), vw - s.x);
    if (kind.includes('s')) h = Math.min(Math.max(s.h + dy, MIN_H), vh - s.y);
    if (kind.includes('w')) {
        const right = s.x + s.w;
        x = Math.min(Math.max(s.x + dx, 0), right - MIN_W);
        w = right - x;
    }
    if (kind.includes('n')) {
        const bottom = s.y + s.h;
        y = Math.min(Math.max(s.y + dy, 0), bottom - MIN_H);
        h = bottom - y;
    }
    return { x, y, w, h };
};

const CURSOR: Record<DragKind, string> = {
    move: 'move',
    n: 'ns-resize',
    s: 'ns-resize',
    e: 'ew-resize',
    w: 'ew-resize',
    ne: 'nesw-resize',
    sw: 'nesw-resize',
    nw: 'nwse-resize',
    se: 'nwse-resize',
};

interface Saved {
    floating: boolean;
    box: Box;
}

const load = (key: string, dockedWidth: number): Saved => {
    const dflt = { floating: false, box: docked(dockedWidth) };
    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return dflt;
        const v = JSON.parse(raw) as Saved;
        const b = v.box;
        if (![b?.x, b?.y, b?.w, b?.h].every(Number.isFinite)) return dflt;
        return {
            floating: !!v.floating,
            box: v.floating ? clamp(b) : docked(b.w),
        };
    } catch {
        return dflt;
    }
};

const save = (key: string, v: Saved) => {
    try {
        window.localStorage.setItem(key, JSON.stringify(v));
    } catch {
        // 隐私模式等写不进去: 只是下次打开回到默认位置
    }
};

export const useFloatingBox = (storageKey: string, dockedWidth: number) => {
    const [state, setState] = useState<Saved>(() =>
        load(storageKey, dockedWidth),
    );
    const stopRef = useRef<() => void>();

    // 视口变化: 贴边时按原宽度重贴, 浮动时收回视口内
    useEffect(() => {
        const onResize = () =>
            setState((v) => ({
                ...v,
                box: v.floating ? clamp(v.box) : docked(v.box.w),
            }));
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // 拖动途中组件卸载 (如按 Esc 关掉): 摘掉监听, 还原光标
    useEffect(() => () => stopRef.current?.(), []);

    const start = useCallback(
        (kind: DragKind) => (e: React.PointerEvent) => {
            if (e.button !== 0) return;
            // 标题栏上的按钮照常点击, 不触发拖动
            if (kind === 'move' && (e.target as HTMLElement).closest('button'))
                return;
            e.preventDefault();
            const origin = state.box;
            // 贴边时: 拖标题栏 = 变浮动; 拖左边 = 只改宽, 仍贴边
            const floating = state.floating || kind === 'move';
            const sx = e.clientX;
            const sy = e.clientY;
            let last: Saved = state;
            const body = document.body.style;
            const prev = { cursor: body.cursor, userSelect: body.userSelect };
            body.cursor = CURSOR[kind];
            body.userSelect = 'none';
            const onMove = (ev: PointerEvent) => {
                const b = resize(origin, kind, ev.clientX - sx, ev.clientY - sy);
                last = { floating, box: floating ? b : docked(b.w) };
                setState(last);
            };
            const stop = () => {
                window.removeEventListener('pointermove', onMove);
                window.removeEventListener('pointerup', onUp);
                window.removeEventListener('pointercancel', onUp);
                body.cursor = prev.cursor;
                body.userSelect = prev.userSelect;
                stopRef.current = undefined;
            };
            const onUp = () => {
                stop();
                if (last !== state) save(storageKey, last);
            };
            window.addEventListener('pointermove', onMove);
            window.addEventListener('pointerup', onUp);
            window.addEventListener('pointercancel', onUp);
            stopRef.current = stop;
        },
        [state, storageKey],
    );

    /** 贴回右侧 (默认宽度) */
    const dock = useCallback(() => {
        const v = { floating: false, box: docked(dockedWidth) };
        setState(v);
        save(storageKey, v);
    }, [dockedWidth, storageKey]);

    return { box: state.box, floating: state.floating, start, dock };
};

/** 四边四角的缩放手柄 (透明热区, 叠在面板边缘) */
export const RESIZE_HANDLES: { kind: DragKind; className: string }[] = [
    { kind: 'n', className: 'top-0 left-2 right-2 h-1.5 cursor-ns-resize' },
    { kind: 's', className: 'bottom-0 left-2 right-2 h-1.5 cursor-ns-resize' },
    { kind: 'w', className: 'left-0 top-2 bottom-2 w-1.5 cursor-ew-resize' },
    { kind: 'e', className: 'right-0 top-2 bottom-2 w-1.5 cursor-ew-resize' },
    { kind: 'nw', className: 'top-0 left-0 w-3 h-3 cursor-nwse-resize' },
    { kind: 'se', className: 'bottom-0 right-0 w-3 h-3 cursor-nwse-resize' },
    { kind: 'ne', className: 'top-0 right-0 w-3 h-3 cursor-nesw-resize' },
    { kind: 'sw', className: 'bottom-0 left-0 w-3 h-3 cursor-nesw-resize' },
];
